import type {RemoteConfig} from '@wandb/forge-shell-contract';

import type {Surface} from './surface';

/**
 * What the shell's consent loader needs from the deployment. Nothing reads it
 * yet; the loader arrives in a follow-up.
 *
 * `environmentName` selects the OneTrust tenant and the Segment source, by two
 * rules that disagree. See `FORGE_ANALYTICS_ENVIRONMENT` in
 * `deploy/shell/values.yaml` before adding an environment.
 */
export type ShellAnalyticsConfig = {
  environmentName?: string;
  thirdPartyEnabled?: boolean;
};

/**
 * What the shell publishes in its runtime config: one entry per configured
 * remote, holding what that remote's manifest resolved to, including the route
 * claims that say where the remote is served. The pod inlines it into
 * index.html; dev serves it at `/shell-config.js`.
 */
export type ShellConfig = {
  remotes?: Record<string, RemoteConfig>;
  origins?: Partial<Record<Surface, string>>;
  /**
   * Origin of the CoreWeave cloud console for this environment. Infrastructure
   * is a separate product suite, so the shell only asks it whether the viewer
   * has a session there and links out to it.
   */
  infraConsoleOrigin?: string;
  /**
   * When true, admin tools are only shown to admins with an approved Teleport
   * access request. Mirrors the wandb app's ENABLE_TELEPORT_ADMIN_CHECK.
   */
  teleportAdminCheck?: boolean;
  /**
   * Forge environments an admin can hop to from the account selector, by
   * display name. The shell renders one link per entry whose origin is not the
   * current one.
   */
  adminEnvironments?: Record<string, string>;
  analytics?: ShellAnalyticsConfig;
};

export function renderShellConfigScript(config: ShellConfig): string {
  const json = JSON.stringify(config).replaceAll('<', '\\u003c');
  return `window.__FORGE_SHELL_CONFIG__ = ${json};`;
}

export const teleportAdminCheckEnabled = (): boolean =>
  window.__FORGE_SHELL_CONFIG__?.teleportAdminCheck === true;

export const adminEnvironments = (): Record<string, string> =>
  window.__FORGE_SHELL_CONFIG__?.adminEnvironments ?? {};

/**
 * A remote's name, as the pod publishes it. Which names exist is decided at
 * deploy time, not build time, so this is whatever key the config carries.
 */
export type RemoteName = keyof NonNullable<ShellConfig['remotes']>;

declare global {
  interface Window {
    __FORGE_SHELL_CONFIG__?: ShellConfig;
  }
}
